import {
  Card,
  CardTypeEnum,
  FlowStep,
  Query,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { WalletButtonProps } from 'features/cardManagement/components/WalletButton/WalletButtonShared';
import useGetFlowStepId from 'features/onboarding/hooks/useGetFlowStepId';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import FetchAccountIDCards from 'services/graphql/queries/FetchAccountIDCards';
import Button from 'shared/components/vxl/Button/Button';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { AnyVariables, useQuery } from 'urql';

const WalletProvisioningScreen = () => {
  const { t: translatedText } = useTranslation('onboarding');

  const flowStepId = useGetFlowStepId();

  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();

  const [{ data, fetching }] = useQuery<Query, AnyVariables>({
    query: FetchAccountIDCards,
    requestPolicy: 'cache-first',
  });

  const cardData = data?.cards?.find(
    (card) => card.type === CardTypeEnum.DebitCard,
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: '/wallet-provisioning',
  });

  const handleSkipOrSubmitButton = useCallback(
    async (didSkip?: boolean) => {
      const input: UpsertFlowStepInput = {
        id: flowStepId,
        intentBasedOnboarding: {
          selection: undefined,
          didSkip: Boolean(didSkip),
        },
      };
      try {
        const result = await submitUpsertFlowStep({ input });
        if (!result.error) {
          redirectUserToTargetRoute(result.data?.upsertFlowStep as FlowStep);
        }
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }
      }
    },
    [flowStepId, submitUpsertFlowStep],
  );

  // Build WalletButtonProps for the ActionScreen add wallet button
  const handleWalletButton = (cardData: Card): WalletButtonProps => {
    return {
      cardId: cardData.id,
      maskedCardNumber: cardData.maskedCardNumber,
      cardholderName: cardData.cardHolderName?.full ?? '',
      isDebit: cardData.type === CardTypeEnum.DebitCard,
      onFlowComplete: () => {
        void handleSkipOrSubmitButton();
      },
      style: { flex: 1, height: 40 },
      sentryPageName: 'Intent-Based Onboarding',
      analyticsContextOverride: {
        sourceName: 'wallet_provisioning',
        category: 'onboarding',
        subCategory: 'intent_based_onboarding',
      },
    };
  };

  return (
    <View style={styles.container}>
      <NavigationBar
        title=""
        trailingAccessories={
          <Button
            label={translatedText('shared.skip')}
            size="SMALL"
            type="TEXT"
            width="HUG"
            onPress={() => handleSkipOrSubmitButton(true)}
            analyticsId="skipWalletProvisioning"
          />
        }
      />
      <ActionScreen
        addWalletButton={cardData ? handleWalletButton(cardData) : undefined}
        titleText={translatedText('walletProvisioning.titleText')}
        visual="DEBIT_CARD_A_ANIMATED"
        bodyContent={
          <BodyBaseText>
            {translatedText('walletProvisioning.bodyText')}
          </BodyBaseText>
        }
        titleTextStyle="extra-large"
      />
      <ScreenTransitionOverlay
        isLoading={fetching || isUpsertFlowStepLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default WalletProvisioningScreen;
